import React from 'react';
import { useNavigate } from 'react-router-dom';
import UICollapsibleSection from '@/components/shared/UICollapsibleSection';
import UIButton from '@/components/shared/UIButton';
import { type TicketDetails } from '@/types/tickets';
import { ReactComponent as InfoIcon } from '@/img/button-icon-info.svg';
import { ReactComponent as LocationIcon } from '@/img/button-icon-location.svg';
import { ReactComponent as ContactIcon } from '@/img/button-icon-contact.svg';
import './TicketsOverviewSection.scss';

export interface TicketOverviewProps {
  /**
   * Об'єкт тікета з деталями
   */
  ticket?: TicketDetails | null;

  /**
   * Стан завантаження
   */
  loading?: boolean;

  /**
   * Контрольований стан згорнутості
   */
  collapsed?: boolean;

  /**
   * Додатковий CSS клас
   */
  className?: string;

  /**
   * Обробник зміни стану згорнутості
   */
  onToggleCollapse?: (collapsed: boolean) => void;
}

/**
 * Інтерфейс для відображення поля інформації
 */
interface TicketInfoField {
  label: string;
  value: string | number | null;
  renderValue?: () => React.ReactNode;
}

const baseClass = 'TicketOverviewSection';

/**
 * Компонент для відображення огляду тікета з усією важливою інформацією
 */
const TicketOverview: React.FC<TicketOverviewProps> = ({
                                                         ticket,
                                                         loading = false,
                                                         collapsed,
                                                         className = '',
                                                         onToggleCollapse,
                                                       }) => {
  const navigate = useNavigate();

  /**
   * Форматує дату для відображення
   */
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-CA'); // YYYY-MM-DD format
  };

  /**
   * Повертає інформацію про тенанта
   */
  const getTenantInfo = () => {
    if (!ticket?.lease?.tenant) return null;

    const tenant = ticket.lease.tenant;
    return {
      name: `${tenant.firstname} ${tenant.lastname}`.trim(),
      email: tenant.email,
      phone: tenant.phone,
      avatar: tenant.avatar,
      tenantId: tenant.id,
    };
  };

  /**
   * Повертає інформацію про property
   */
  const getPropertyInfo = () => {
    if (!ticket?.lease?.unit?.property) return null;

    const unit = ticket.lease.unit;
    const property = unit.property;

    return {
      property: property,
      unit: unit,
      // Тут можна додати адресу якщо вона є в структурі
      address: 'Address not available', // Placeholder
    };
  };

  /**
   * Повертає поля деталей тікета
   */
  const getTicketDetailsFields = (): TicketInfoField[] => {
    if (!ticket) return [];

    // Форматуємо категорію
    const formatCategory = (category: string): string => {
      return category.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    };

    // Форматуємо статус для відображення
    const formatStatus = (status: string): string => {
      return status.replace(/_/g, ' ').toUpperCase();
    };

    const leftColumnFields: TicketInfoField[] = [
      {
        label: 'Category:',
        value: ticket.category,
        renderValue: () => (
          <div className={`${baseClass}__category-badge`}>
            {formatCategory(ticket.category)}
          </div>
        ),
      },
      {
        label: 'Subject:',
        value: ticket.title,
        renderValue: () => (
          <div className={`${baseClass}__subject-badge`}>
            {ticket.title}
          </div>
        ),
      },
      {
        label: 'Status:',
        value: ticket.status,
        renderValue: () => (
          <div className={`${baseClass}__status-badge ${baseClass}__status-badge--${ticket.status.toLowerCase()}`}>
            {formatStatus(ticket.status)}
          </div>
        ),
      },
    ];

    const rightColumnFields: TicketInfoField[] = [
      {
        label: 'Created:',
        value: formatDate(ticket.createdAt),
      },
      {
        label: 'Due date:',
        value: ticket.updatedAt ? formatDate(ticket.updatedAt) : 'N/A', // Placeholder, нема due date в типі
      },
    ];

    return [...leftColumnFields, ...rightColumnFields];
  };

  /**
   * Обробляє натискання кнопки "Open conversation"
   */
  const handleOpenConversation = () => {
    if (!ticket?.lease?.tenant?.id) return;

    // Перехід до чату з тенантом
    navigate(`/dashboard/chats?chatId=${ticket.lease.tenant.id}`);
  };

  const tenantInfo = getTenantInfo();
  const propertyInfo = getPropertyInfo();
  const ticketDetailsFields = getTicketDetailsFields();

  // Розділяємо поля на дві колонки
  const leftColumnFields = ticketDetailsFields.slice(0, 3);
  const rightColumnFields = ticketDetailsFields.slice(3);

  const renderTicketInfo = () => (
    <div className={`${baseClass}__info`}>
      {/* Секція з інформацією про тенанта та property */}
      <div className={`${baseClass}__header-section`}>
        {/* Інформація про тенанта */}
        <div className={`${baseClass}__tenant-section`}>
          <div className={`${baseClass}__field-labels`}>
            <div className={`${baseClass}__field-name`}>Tenant</div>
            <div className={`${baseClass}__field-name`}>Phone:</div>
            <div className={`${baseClass}__field-name`}>Email:</div>
          </div>
          <div className={`${baseClass}__field-values`}>
            <div className={`${baseClass}__field-value`}>
              {tenantInfo ? tenantInfo.name : 'N/A'}
            </div>
            <div className={`${baseClass}__field-value`}>
              {tenantInfo?.phone || 'N/A'}
            </div>
            <div className={`${baseClass}__field-value ${baseClass}__field-value--link`}>
              {tenantInfo ? tenantInfo.email : 'N/A'}
            </div>
          </div>
        </div>

        {/* Інформація про property */}
        <div className={`${baseClass}__property-section`}>
          <div className={`${baseClass}__field-labels`}>
            <div className={`${baseClass}__field-name`}>Property:</div>
            <div className={`${baseClass}__field-name`}>Address:</div>
            <div className={`${baseClass}__field-name`}>Unit:</div>
          </div>
          <div className={`${baseClass}__field-values`}>
            <div className={`${baseClass}__field-value ${baseClass}__field-value--link`}>
              <InfoIcon
                className={`${baseClass}__property-icon`}
                stroke="#105E96"
                fill="none"
              />
              {propertyInfo ? propertyInfo.property.name : 'N/A'}
            </div>
            <div className={`${baseClass}__field-value`}>
              {propertyInfo?.address || 'Address not available'}
            </div>
            <div className={`${baseClass}__field-value ${baseClass}__field-value--link`}>
              <InfoIcon
                className={`${baseClass}__unit-icon`}
                stroke="#105E96"
                fill="none"
              />
              {propertyInfo ? propertyInfo.unit.number : 'N/A'}
            </div>
          </div>
        </div>
      </div>

      {/* Секція з деталями тікета */}
      <div className={`${baseClass}__details-section`}>
        <div className={`${baseClass}__details-table`}>
          {/* Ліва колонка */}
          <div className={`${baseClass}__details-column`}>
            <div className={`${baseClass}__field-labels`}>
              {leftColumnFields.map((field, index) => (
                <div key={index} className={`${baseClass}__field-name`}>
                  {field.label}
                </div>
              ))}
            </div>
            <div className={`${baseClass}__field-values`}>
              {leftColumnFields.map((field, index) => (
                <div key={index} className={`${baseClass}__field-value`}>
                  {field.renderValue ? field.renderValue() : (field.value || 'N/A')}
                </div>
              ))}
            </div>
          </div>

          {/* Права колонка */}
          <div className={`${baseClass}__details-column`}>
            <div className={`${baseClass}__field-labels`}>
              {rightColumnFields.map((field, index) => (
                <div key={index} className={`${baseClass}__field-name`}>
                  {field.label}
                </div>
              ))}
            </div>
            <div className={`${baseClass}__field-values`}>
              {rightColumnFields.map((field, index) => (
                <div key={index} className={`${baseClass}__field-value`}>
                  {field.renderValue ? field.renderValue() : (field.value || 'N/A')}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Секція опису */}
      {ticket?.description && (
        <div className={`${baseClass}__description-section`}>
          <div className={`${baseClass}__field-name`}>Description</div>
          <div className={`${baseClass}__description-text`}>
            {ticket.description}
          </div>
        </div>
      )}

      {/* Кнопка відкриття розмови */}
      {tenantInfo && (
        <div className={`${baseClass}__actions`}>
          <UIButton
            variant="success"
            onClick={handleOpenConversation}
            className={`${baseClass}__conversation-btn`}
          >
            <ContactIcon
              className={`${baseClass}__conversation-icon`}
              fill="white"
              stroke="none"
            />
            Open conversation
          </UIButton>
        </div>
      )}
    </div>
  );

  return (
    <UICollapsibleSection
      title="Ticket overview"
      description=""
      collapsible={true}
      collapsed={collapsed}
      onToggleCollapse={onToggleCollapse}
      loading={loading}
      loadingText="Loading ticket information..."
      className={`${baseClass} ${className}`.trim()}
    >
      {renderTicketInfo()}
    </UICollapsibleSection>
  );
};

export default TicketOverview;